import {
  Container,
  Flex,
  PrimaryButton,
  SecondaryButton,
  Text,
  LegacyText,
  TransparentButton,
} from "flicket-ui";
import React from "react";
import { css } from "styled-components";
import { Icon, Modal } from "~components";

type CheckoutModalProps = {
  variant: CheckoutModalVariants;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  additionalContext?: JSX.Element;
  descriptionOverride?: string;
};

interface ModalData {
  title: string;
  description: string;
  confirmButton?: string;
  closeButton: string;
}

type CheckoutModalVariants =
  | "leave-checkout"
  | "nonseated-unmet-purchase-restrictions"
  | "seated-unmet-purchase-restrictions"
  | "hold-order";

const modalData: Record<CheckoutModalVariants, ModalData> = {
  "leave-checkout": {
    title: "Leave Checkout",
    description:
      "Are you sure you want to leave checkout? Your tickets will be released and may not be available later. Complete your order now to secure your tickets",
    closeButton: "Stay",
    confirmButton: "Leave",
  },
  "nonseated-unmet-purchase-restrictions": {
    title: "Unmet Purchase Restrictions",
    description:
      "Please enter an email address which meets the ticket purchase restrictions.",
    closeButton: "Okay",
  },
  "seated-unmet-purchase-restrictions": {
    title: "Unmet Purchase Restrictions",
    description:
      "Please select tickets which meet the ticket purchase restrictions.",
    closeButton: "Okay",
  },
  "hold-order": {
    title: "Leave Checkout",
    description:
      "Are you sure you want to leave the checkout? Your order will be remain on hold and you can return to complete it later.",
    closeButton: "Close",
    confirmButton: "Leave",
  },
};

export const CheckoutModal = ({
  isOpen,
  onClose,
  onConfirm,
  variant,
  additionalContext,
  descriptionOverride,
}: CheckoutModalProps) => {
  const { title, description, closeButton, confirmButton } = modalData[variant];
  return (
    <Modal isOpen={isOpen} onClose={onClose} aria-label="checkout-modal">
      <Container
        px={{ _: 0, md: 2 }}
        minHeight={{ _: "100%", md: "auto" }}
        innerProps={{
          p: { _: 2, md: 4 },
          minHeight: { _: "100%", md: "auto" },
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
        // max-width needs to be set as a css property to override the
        // property defined in flicket-ui's Container component
        css={css({
          maxWidth: "800px",
        })}
      >
        <Flex
          flexDir={"row"}
          alignItems="center"
          justifyContent={"space-between"}
        >
          <LegacyText fontWeight="heavy" fontSize={6}>
            {title}
          </LegacyText>
          <TransparentButton onClick={onClose}>
            <Icon icon="close" fontSize={5} />
          </TransparentButton>
        </Flex>
        <LegacyText mt={3}>{descriptionOverride ?? description}</LegacyText>
        {additionalContext && (
          <LegacyText mt={3}>{additionalContext}</LegacyText>
        )}
        <Flex mt={3} justifyContent="flex-end">
          {confirmButton && (
            <SecondaryButton onClick={() => onConfirm()} mr={3}>
              {confirmButton}
            </SecondaryButton>
          )}
          <PrimaryButton onClick={onClose}>{closeButton}</PrimaryButton>
        </Flex>
      </Container>
    </Modal>
  );
};
